// SIMS-9071 (PRD v0.3 FR-12, decision D11) — every message option on a PAYING prize tier must
// state the amount. This is the FORM's copy of a rule also enforced server-side
// (cmi-backend-lambdas-sims-application-02/libs/shared/src/helpers/fishbowl-prize-message.helper.ts).
//
// Both exist for the same reason the probability pair does (see fishbowlPrizeDistribution.ts):
// the server rule is the one that HOLDS, because the Treatment Config assistant's schema
// deliberately does not model rewardCustomConfig; the form rule is what the configurator SEES,
// before a 4xx. The two are worded identically so the rule is never stated two ways.
//
// Running example: A $0.00 "Better luck next time" -> exempt (AC3)
//                  B $5.00 "You won [AMOUNT]!"     -> compliant (renders "You won $5.00!")
//                  C $12.75 "Big win!"             -> blocked, names tier C
//
// Portal Q-16 (option A): [AMOUNT] OWNS the currency symbol. The server expands it to the
// account's symbol plus the amount, so the configurator types no symbol of their own.

/**
 * Matched EXACTLY. The server substitutes with a case-sensitive `String.replace("[AMOUNT]", ...)`
 * (fishbowl-draw-resolver.helper.ts, since SIMS-9073 moved resolution off the browser), so
 * "[amount]" would save a message that can never expand — the participant would see the literal
 * token where their money should be, which is the failure FR-12 exists to prevent.
 */
export const AMOUNT_PLACEHOLDER = "[AMOUNT]";

interface PrizeTierLike {
	amount?: unknown;
	displayMessage?: unknown;
	// SIMS-9080 (portal D-95): present only with two or more options, in display order.
	displayMessages?: unknown;
}

interface FishbowlConfigLike {
	name?: string;
	rewardCustomConfig?: {
		fishbowlType?: string;
		prizeDistributions?: PrizeTierLike[];
	} | null;
	additionalTreatmentConfigs?: FishbowlConfigLike[] | null;
}

/**
 * The message options a tier carries, in display order — the form's copy of the server's
 * prizeMessageOptions() (fishbowl-prize-message.helper.ts), read the same way.
 *
 * SIMS-9080 (portal D-95): `displayMessages` when it is a non-empty list of text, otherwise the
 * single `displayMessage`. A tier saved before this release therefore reads as its one option
 * (AC5) with nothing converted. A non-string message yields no option rather than being coerced:
 * `String(42)` would produce a message that silently never expands.
 */
export function prizeMessageOptions(tier?: PrizeTierLike | null): string[] {
	const list = tier?.displayMessages;
	if (
		Array.isArray(list) &&
		list.length > 0 &&
		list.every((option) => typeof option === "string")
	) {
		return [...(list as string[])];
	}
	const single = tier?.displayMessage;
	return typeof single === "string" ? [single] : [];
}

/**
 * The tier with `options` written the D-95 way. One option -> `displayMessage` only and any
 * `displayMessages` removed (the legacy shape, so an untouched single-message tier stays
 * byte-identical). Two or more -> `displayMessages` in display order, with `displayMessage`
 * mirroring option 1 for anything still reading it. No index, number or id is written (D5: the
 * reference number is a display label, not stored data).
 */
export function withMessageOptions<T extends PrizeTierLike>(
	tier: T,
	options: string[],
): T {
	const { displayMessages: _dropped, ...rest } = tier;
	void _dropped;
	if (options.length <= 1) {
		return { ...rest, displayMessage: options[0] ?? "" } as T;
	}
	return {
		...rest,
		displayMessage: options[0],
		displayMessages: [...options],
	} as T;
}

/**
 * Portal D-100 — the 0-based index of the first blank option of a tier with two or more options,
 * or -1. A blank option would show the participant nothing on the draws it is picked for, so it
 * blocks the save whatever the tier pays. A single-option tier keeps today's behaviour.
 */
export function firstBlankMessageOption(options: string[]): number {
	if (options.length < 2) return -1;
	return options.findIndex((option) => option.trim().length === 0);
}

/** 0-based indices of the options that omit the placeholder — AC4's "offending option". */
export function optionsMissingAmountPlaceholder(options: string[]): number[] {
	const missing: number[] = [];
	options.forEach((option, index) => {
		if (!option.includes(AMOUNT_PLACEHOLDER)) {
			missing.push(index);
		}
	});
	return missing;
}

/**
 * AC3's exemption, keyed on the AMOUNT rather than on position or name — so one cent is not
 * exempt and nothing drifts out of the rule by being "zero-ish". An absent or non-numeric
 * amount is not configured as paying anything, so FR-12 has nothing to say about it.
 */
export function prizeTierPays(tier?: PrizeTierLike | null): boolean {
	const amount = Number(tier?.amount);
	return Number.isFinite(amount) && amount > 0;
}

/**
 * Whether THIS tier, as currently configured, breaks the rule — used to mark the message field
 * while the configurator is still typing, rather than only at save.
 */
export function prizeTierMessageIsIncomplete(
	tier?: PrizeTierLike | null,
): boolean {
	if (!prizeTierPays(tier)) {
		return false;
	}
	const options = prizeMessageOptions(tier);
	return (
		options.length === 0 || optionsMissingAmountPlaceholder(options).length > 0
	);
}

/**
 * A currency symbol typed immediately before the placeholder — "$[AMOUNT]", "£ [AMOUNT]".
 *
 * An explicit set rather than Unicode's `\p{Sc}` class: this repo compiles to ES5, where the `u`
 * flag that class needs is unavailable. The set covers the dollar family (including the trailing
 * `$` of "R$" or "US$"), pound, euro, yen/yuan, cent, rupee, won, ruble, peso, shekel, dong,
 * naira, hryvnia, lira and colón.
 */
const SYMBOL_BEFORE_PLACEHOLDER = /[$£€¥¢₹₩₽₱₪₫₦₴₺₡]\s*\[AMOUNT\]/;

/**
 * Portal Q-16 (option A) — the configurator typed a currency symbol of their own in front of
 * `[AMOUNT]`, which already supplies one, so the participant would read "$$5.00".
 *
 * A WARNING, never a block. Q-16 decided the guidance changes and saved messages are left alone
 * (OQ-2: no retroactive treatment), so a plan carrying an old "$[AMOUNT]" message must still
 * open and save. Exactly what SIMS-9079 AC4 protects. The warning is what gets it edited out.
 */
export function prizeTierMessageHasTypedCurrency(
	tier?: PrizeTierLike | null,
): boolean {
	return prizeMessageOptions(tier).some((option) =>
		SYMBOL_BEFORE_PLACEHOLDER.test(option),
	);
}

/**
 * Gate for the save path: the first offending configuration across every template and its
 * additional configs, or null when all are valid. Mirrors the server helper's wording exactly,
 * including the deliberate skip of an EMPTY tier list ("not configured yet" is not "configured
 * wrongly").
 */
export function fishbowlMessageErrorMessage(
	configs?: FishbowlConfigLike[] | null,
): string | null {
	if (!Array.isArray(configs)) {
		return null;
	}

	const flattened: FishbowlConfigLike[] = [];
	configs.forEach((config) => {
		if (!config) {
			return;
		}
		flattened.push(config);
		if (Array.isArray(config.additionalTreatmentConfigs)) {
			config.additionalTreatmentConfigs.forEach((additional) => {
				if (additional) {
					flattened.push(additional);
				}
			});
		}
	});

	for (const config of flattened) {
		const reward = config.rewardCustomConfig;
		if (!reward || reward.fishbowlType !== "DIGITAL") {
			continue;
		}
		const tiers = reward.prizeDistributions;
		if (!Array.isArray(tiers) || tiers.length === 0) {
			continue;
		}

		for (let i = 0; i < tiers.length; i++) {
			const label = config.name?.trim();
			const where = label ? `, in "${label}"` : "";

			// SIMS-9080 — a blank option is the more basic fault, so it is reported first and for
			// every tier, whatever it pays (portal D-100). Same wording as the server.
			const blank = firstBlankMessageOption(prizeMessageOptions(tiers[i]));
			if (blank >= 0) {
				return (
					`Prize tier ${i + 1}: message option ${blank + 1} is empty${where}. ` +
					"Write it or remove it."
				);
			}

			if (!prizeTierMessageIsIncomplete(tiers[i])) {
				continue;
			}

			const options = prizeMessageOptions(tiers[i]);

			if (options.length > 1) {
				const missing = optionsMissingAmountPlaceholder(options);
				return (
					`Prize tier ${i + 1} pays more than zero, so every message option must include ` +
					`the ${AMOUNT_PLACEHOLDER} placeholder. Option ${missing[0] + 1} does not${where}.`
				);
			}

			return (
				`Prize tier ${i + 1} pays more than zero, so its message must include the ` +
				`${AMOUNT_PLACEHOLDER} placeholder${where}.`
			);
		}
	}

	return null;
}
